import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import TeamScheduleCard from "@/components/TeamScheduleCard";
import {
  CurrencyDollar, Warning, Users, Briefcase, TrendUp, CalendarBlank,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

const STATUS_COLORS = {
  unscheduled: "#94A3B8", scheduled: "#00B4D8", in_progress: "#F59E0B",
  completed: "#10B981", cancelled: "#F43F5E",
};

function Kpi({ icon: Icon, label, value, accent, delay, onClick }) {
  return (
    <div
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-white border border-slate-200 rounded-lg p-5 shadow-sm fade-up ${onClick ? "cursor-pointer hover:border-[#00B4D8] hover:shadow-md transition-all" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon size={20} weight="duotone" className="text-white" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-extrabold text-slate-900 tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.dashboard().then(setStats).catch(() => {}); }, []);

  if (!stats) return <div className="p-10 text-slate-400">Loading dashboard…</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <PageHeader title="Command Center" subtitle="Everything happening across All Day Power Washing" />

      {/* Upcoming jobs strip */}
      <div className="bg-[#0B1726] rounded-lg p-5 mb-6 fade-up">
        <div className="flex items-center gap-2 mb-4">
          <CalendarBlank size={18} weight="duotone" className="text-[#00B4D8]" />
          <h3 className="text-white font-display font-semibold">Today & Upcoming</h3>
        </div>
        {stats.upcoming_jobs.length === 0 ? (
          <p className="text-slate-400 text-sm">No upcoming scheduled jobs. Time to book some work!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.upcoming_jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => navigate("/jobs")}
                data-testid={`upcoming-job-${j.id}`}
                className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-md p-3 transition-colors duration-200"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[#00B4D8] text-xs font-bold">{j.number}</span>
                  <span className="text-slate-400 text-xs">{j.scheduled_date} {j.scheduled_time}</span>
                </div>
                <div className="text-white font-semibold text-sm mt-1 truncate">{j.title}</div>
                <div className="text-slate-400 text-xs truncate">{j.client_name}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={CurrencyDollar} label="Revenue Collected" value={money(stats.total_revenue)} accent="bg-emerald-500" delay={0} onClick={() => navigate("/invoices?status=paid")} />
        <Kpi icon={Warning} label="Outstanding" value={money(stats.outstanding)} accent="bg-orange-500" delay={60} onClick={() => navigate("/invoices?status=sent")} />
        <Kpi icon={Users} label="Clients" value={stats.clients_count} accent="bg-[#00B4D8]" delay={120} onClick={() => navigate("/clients")} />
        <Kpi icon={Briefcase} label="Jobs Completed" value={stats.jobs_completed} accent="bg-[#0B1726]" delay={180} onClick={() => navigate("/jobs?status=completed")} />
      </div>

      {/* Team & assignments (admin only) */}
      {user?.role === "admin" && <TeamScheduleCard />}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5 shadow-sm fade-up">
          <div className="flex items-center gap-2 mb-5">
            <TrendUp size={18} weight="duotone" className="text-[#00B4D8]" />
            <h3 className="font-display font-semibold text-slate-900">Revenue — Last 6 Months</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.revenue_series} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00B4D8" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00B4D8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Area type="monotone" dataKey="revenue" stroke="#00B4D8" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm fade-up">
          <h3 className="font-display font-semibold text-slate-900 mb-5">Jobs by Status <span className="text-xs font-normal text-slate-400">(click a bar to view)</span></h3>
          {stats.jobs_by_status.length === 0 ? (
            <p className="text-slate-400 text-sm">No jobs yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stats.jobs_by_status} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="status" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} cursor={{ fill: "#F8FAFC" }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => d && navigate(`/jobs?status=${d.status}`)}>
                  {stats.jobs_by_status.map((e, i) => (<Cell key={i} fill={STATUS_COLORS[e.status] || "#94A3B8"} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
